import { PopoverPlacement } from '@idui/react-popover';
import ButtonWithHelp, { ButtonWithHelpProps } from 'components/atoms/ButtonWithHelp';
import { toast } from 'react-toastify';
import { CloudinaryDto } from 'utils/mms';

export interface MediaUploadResult {
  event?: string;
  info?: {
    id?: string;
    public_id: string;
    bytes?: number;
    secure_url?: string;
    url: string;
    path?: string;
    thumbnail_url?: string;
    playback_url?: string;
    delete_token?: string;
    coordinates?: {
      custom: [][number];
    };
  };
}

interface CloudinaryUploaderOptions {
  apiKey?: string;
  cloudName: string;
  cropping: boolean;
  croppingAspectRatio?: number;
  croppingCoordinatesMode?: string;
  language: string;
  text: string;
  multiple: boolean;
  maxFiles: number;
  showPoweredBy?: boolean;
  showSkipCropButton?: boolean;
  uploadPreset: string;
  resourceType?: string;
  styles?: any;
  sources?: string[];
  clientAllowedFormats?: string[];
  maxFileSize?: number;
  defaultSource?: 'camera' | 'local';
}

const getCloudinaryUploaderOptions = (props: Partial<CloudinaryUploaderOptions>): CloudinaryUploaderOptions => {
  const maxFiles = props.maxFiles || 1;
  const cropping = maxFiles <= 1;
  const clientAllowedFormats = props.clientAllowedFormats || ['image'];
  const maxFileSize = props.maxFileSize || null;

  return {
    apiKey: process.env.REACT_APP_CLOUDINARY_CLOUD_ID || '',
    cloudName: process.env.REACT_APP_CLOUDINARY_CLOUD_ID || 'gloo-dev',
    uploadPreset: process.env.REACT_APP_CLOUDINARY_UNSIGNED_UPLOAD_PRESET || '',
    sources: ['local'],
    resourceType: 'image',
    defaultSource: 'local',
    cropping,
    croppingAspectRatio: 1.6,
    croppingCoordinatesMode: 'custom',
    language: 'en',
    text: 'Upload Media',
    multiple: false,
    maxFiles,
    showPoweredBy: false,
    clientAllowedFormats,
    maxFileSize,
    showSkipCropButton: true,
    ...props,
  };
};

interface CloudinaryMediaUploaderProps extends Omit<ButtonWithHelpProps, 'onClick' | 'dataDurableId' | 'helpText'> {
  helpText?: string;
  onMediaUpload: (mediaRef: MediaUploadResult) => any;
  uploaderOptions?: Partial<CloudinaryUploaderOptions>;
}

export const CloudinaryMediaUploader = ({ onMediaUpload, uploaderOptions, ...rest }: CloudinaryMediaUploaderProps) => {
  const cloudinaryWidgetEventHandler = (
    error: object,
    result: MediaUploadResult,
    onUploadCallback: (data: MediaUploadResult) => void,
  ): void => {
    if (result.event === 'success') {
      toast.success(`Your media successfully uploaded`);
      onUploadCallback(result);
    } else if (error) {
      toast.error(`Media could not be uploaded`);
    }
  };

  const createCloudinaryWidget = (
    updateImage: (data: MediaUploadResult) => void,
    maxFiles = 1,
    croppingAspectRatio = 1.6,
  ): CloudinaryDto | undefined => {
    const options = getCloudinaryUploaderOptions({ maxFiles, croppingAspectRatio, ...uploaderOptions });
    const callback = (error: object, result: MediaUploadResult): void =>
      cloudinaryWidgetEventHandler(error, result, updateImage);

    return (window as any).cloudinary?.createUploadWidget(options, callback);
  };

  const openWidget = (): void => {
    const widget = createCloudinaryWidget(onMediaUpload, 1, 1);
    if (widget) widget.open();
  };

  return (
    <ButtonWithHelp
      helpOptions={{
        disabled: rest.disabled,
        inline: true,
        flex: true,
        fitMaxWidthToBounds: true,
        mobilePlacement: PopoverPlacement.right,
      }}
      type="transparent"
      helpText="Upload Image"
      dataDurableId="CloudinaryMediaUploaderButton"
      {...rest}
      onClick={openWidget}
    />
  );
};
